import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Login from './components/Login';
import SRHDashboard from './components/SRHDashboard';
import WINLIFEDashboard from './components/WINLIFEDashboard';
import AdminDashboard from './components/AdminDashboard';
import InstallPrompt from './components/InstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import ImpersonationBanner from './components/ImpersonationBanner';
import { SupplierOrdersReportPage } from './components/SupplierOrdersReport';
import AccountsReport from './components/AccountsReport';
import PriceListReport from './components/PriceListReport';
import '@/App.css';

// Wrapper component for supplier report page (reads data from localStorage)
const SupplierReportWrapper = () => {
  const [data, setData] = useState({ orders: [], stockData: [] });

  useEffect(() => {
    const orders = localStorage.getItem('supplierReportOrders');
    const stock = localStorage.getItem('supplierReportStock');
    if (orders) {
      setData({
        orders: JSON.parse(orders),
        stockData: stock ? JSON.parse(stock) : []
      });
    }
  }, []);

  if (data.orders.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <p className="text-xl text-slate-600">No report data found</p>
          <p className="text-sm text-slate-500 mt-2">Please generate report from Orders panel</p>
        </div>
      </div>
    );
  }

  return <SupplierOrdersReportPage orders={data.orders} stockData={data.stockData} />;
};

function App() {
  const [user, setUser] = useState(null);
  // Saved admin session (when impersonating, we stash the admin's token here)
  const [adminSession, setAdminSession] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const username = localStorage.getItem('username');
    const role = localStorage.getItem('role');
    const permissions = localStorage.getItem('permissions');
    const impersonatedBy = localStorage.getItem('impersonated_by');
    if (token && username && role) {
      setUser({ 
        username, 
        role, 
        token, 
        permissions: permissions ? JSON.parse(permissions) : {},
        ...(impersonatedBy ? { is_impersonation: true, impersonated_by: impersonatedBy } : {})
      });
    }
    // Restore stashed admin session if present
    const stashed = localStorage.getItem('admin_session_stash');
    if (stashed) {
      try { setAdminSession(JSON.parse(stashed)); } catch (_) {}
    }

    // Setup axios interceptor for 401 errors
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          // Token expired or invalid
          console.error('Authentication error:', error.response.data);
          toast.error('Session expired. Please login again.');
          
          // Clear user data and redirect to login
          setUser(null);
          localStorage.removeItem('token');
          localStorage.removeItem('username');
          localStorage.removeItem('role');
          
          // Reload to login page
          window.location.href = '/';
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('token', userData.token);
    localStorage.setItem('username', userData.username);
    localStorage.setItem('role', userData.role);
    localStorage.setItem('permissions', JSON.stringify(userData.permissions || {}));
  };

  const handleLogout = () => {
    setUser(null);
    setAdminSession(null);
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('permissions');
    localStorage.removeItem('impersonated_by');
    localStorage.removeItem('admin_session_stash');
  };

  // Start impersonation: stash admin session, switch user to target
  const startImpersonation = (targetUserData) => {
    if (!user) return;
    const stash = {
      token: user.token,
      username: user.username,
      role: user.role,
      permissions: user.permissions || {},
    };
    setAdminSession(stash);
    localStorage.setItem('admin_session_stash', JSON.stringify(stash));
    // Apply target user
    setUser({
      username: targetUserData.username,
      role: targetUserData.role,
      token: targetUserData.token,
      permissions: targetUserData.permissions || {},
      is_impersonation: true,
      impersonated_by: targetUserData.impersonated_by || stash.username,
    });
    localStorage.setItem('token', targetUserData.token);
    localStorage.setItem('username', targetUserData.username);
    localStorage.setItem('role', targetUserData.role);
    localStorage.setItem('permissions', JSON.stringify(targetUserData.permissions || {}));
    localStorage.setItem('impersonated_by', targetUserData.impersonated_by || stash.username);
    // Navigate to dashboard root
    window.location.href = '/dashboard';
  };

  // Exit impersonation: restore admin session
  const stopImpersonation = () => {
    if (!adminSession) return;
    setUser({
      username: adminSession.username,
      role: adminSession.role,
      token: adminSession.token,
      permissions: adminSession.permissions || {},
    });
    localStorage.setItem('token', adminSession.token);
    localStorage.setItem('username', adminSession.username);
    localStorage.setItem('role', adminSession.role);
    localStorage.setItem('permissions', JSON.stringify(adminSession.permissions || {}));
    localStorage.removeItem('impersonated_by');
    localStorage.removeItem('admin_session_stash');
    setAdminSession(null);
    window.location.href = '/dashboard';
  };

  // Expose to children via window (simple but effective; avoids context refactor)
  useEffect(() => {
    window.__startImpersonation = startImpersonation;
    window.__stopImpersonation = stopImpersonation;
    return () => {
      delete window.__startImpersonation;
      delete window.__stopImpersonation;
    };
    // eslint-disable-next-line
  }, [user, adminSession]);

  return (
    <div className={`App ${user?.is_impersonation ? 'pt-10' : ''}`}>
      <BrowserRouter>
        <Routes>
          <Route 
            path="/" 
            element={user ? <Navigate to="/dashboard" /> : <Login onLogin={handleLogin} />} 
          />
          <Route 
            path="/dashboard" 
            element={
              !user ? <Navigate to="/" /> :
              user.role === 'srh' ? <SRHDashboard user={user} onLogout={handleLogout} /> :
              user.role === 'winlife' ? <WINLIFEDashboard user={user} onLogout={handleLogout} /> :
              (user.role === 'admin' || user.role === 'user') ? <AdminDashboard user={user} onLogout={handleLogout} /> :
              <Navigate to="/" />
            } 
          />
          <Route 
            path="/supplier-report" 
            element={<SupplierReportWrapper />} 
          />
          <Route 
            path="/accounts-report" 
            element={<AccountsReport />} 
          />
          <Route 
            path="/price-list-report" 
            element={<PriceListReport />} 
          />
        </Routes>
      </BrowserRouter>
      
      {/* PWA Install Prompt */}
      <InstallPrompt />

      {/* Offline / Reconnect Indicator */}
      <OfflineIndicator />

      {/* Persistent Impersonation Banner */}
      <ImpersonationBanner user={user} />
    </div>
  );
}

export default App;
